import { Project, WallElement, AreaElement } from '../types';
import * as THREE from 'three';

/**
 * 生成唯一的Kimap ID
 * 格式: kimap + 时间戳(36进制) + 随机字符串
 */
export function generateKimapUniqueId(): string {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 10);
  return `kimap${timestamp}${randomStr}`;
}

/**
 * 将3D场景导出为OBJ格式
 */
export class OBJExporter {
  private vertexIndex: number = 1;
  private output: string[] = [];

  /**
   * 计算整个项目的3D边界（基于实际元素）
   */
  private calculateBorder(project: Project): {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
  } {
    let minX = Infinity, minY = 0, minZ = Infinity;
    let maxX = -Infinity, maxY = 0, maxZ = -Infinity;

    // 遍历所有楼层和图层，计算边界
    project.floors.forEach((floor) => {
      floor.layers.forEach((layer) => {
        layer.elements.forEach((element) => {
          // 计算每个元素的点对XZ平面（2D平面）的边界
          element.points.forEach((p) => {
            const x = p.x / 100; // mm to meters
            const z = p.y / 100; // mm to meters, Z轴对应2D的Y
            
            minX = Math.min(minX, x);
            minZ = Math.min(minZ, z);
            maxX = Math.max(maxX, x);
            maxZ = Math.max(maxZ, z);
          });

          // 计算元素高度对Y轴边界的影响
          if (element.type === 'wall') {
            const wallHeight = (element as WallElement).height / 1000; // 墙面高度，mm → m，与3D预览一致
            maxY = Math.max(maxY, wallHeight);
          } else if (element.type === 'rectangle' || element.type === 'circle' || element.type === 'polygon') {
            const LOGICAL_WALL_HEIGHT = 3000;
            const RENDER_WALL_HEIGHT = 0.375;
            const furnitureLogicalHeight = (element as any).height || 750;
            const furnitureHeight = (furnitureLogicalHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;
            maxY = Math.max(maxY, furnitureHeight);
          } else if (element.type === 'stair') {
            const LOGICAL_WALL_HEIGHT = 3000;
            const RENDER_WALL_HEIGHT = 0.375;
            const logicalStairHeight = (element as any).height || 3000;
            const stairHeight = (logicalStairHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;
            maxY = Math.max(maxY, stairHeight);
          }
        });
      });
    });

    // 如果没有找到有效边界，使用默认值
    if (!isFinite(minX) || !isFinite(minZ) || !isFinite(maxX) || !isFinite(maxZ)) {
      return {
        minX: 0,
        minY: 0,
        minZ: 0,
        maxX: 10,
        maxY: 3,
        maxZ: 10,
      };
    }

    return { minX, minY, minZ, maxX, maxY, maxZ };
  }

  /**
   * 导出项目为OBJ格式
   * @param project 项目数据
   * @param mtlFileName 可选的MTL文件名（不含扩展名），用于mtllib引用
   */
  exportProject(project: Project, mtlFileName?: string): string {
    this.vertexIndex = 1;
    this.output = [];

    // 计算模型边界（用于坐标转换）
    const border = this.calculateBorder(project);

    // 添加文件头
    this.output.push('# Kimap 3D Model Export');
    this.output.push(`# Project: ${project.name}`);
    this.output.push(`# Created: ${new Date().toISOString()}`);
    this.output.push('');
    
    // 添加边界信息（供SDK使用）
    this.output.push('# ===== Model Border (for coordinate transformation) =====');
    this.output.push(`# KIMAP_BORDER_MIN_X ${border.minX.toFixed(6)}`);
    this.output.push(`# KIMAP_BORDER_MIN_Y ${border.minY.toFixed(6)}`);
    this.output.push(`# KIMAP_BORDER_MIN_Z ${border.minZ.toFixed(6)}`);
    this.output.push(`# KIMAP_BORDER_MAX_X ${border.maxX.toFixed(6)}`);
    this.output.push(`# KIMAP_BORDER_MAX_Y ${border.maxY.toFixed(6)}`);
    this.output.push(`# KIMAP_BORDER_MAX_Z ${border.maxZ.toFixed(6)}`);
    this.output.push('# ===== End Border Info =====');
    this.output.push('');
    
    // 添加地图配置信息（校准数据）
    if (project.mapConfig.isCalibrated) {
      this.output.push('# ===== Map Config (Calibration Data) =====');
      this.output.push(`# KIMAP_CONFIG_CALIBRATED ${project.mapConfig.isCalibrated}`);
      this.output.push(`# KIMAP_CONFIG_CALIBRATION_SCALE ${project.mapConfig.calibrationScale?.toFixed(4) || 0}`);
      this.output.push(`# KIMAP_CONFIG_REAL_WIDTH ${project.mapConfig.realWidth?.toFixed(2) || 0}`);
      this.output.push(`# KIMAP_CONFIG_REAL_HEIGHT ${project.mapConfig.realHeight?.toFixed(2) || 0}`);
      this.output.push(`# KIMAP_CONFIG_MODEL_TO_REAL_SCALE ${project.mapConfig.modelToRealScale?.toFixed(4) || 1}`);
      this.output.push('# ===== End Config Info =====');
      this.output.push('');
    }
    
    // 引用MTL材质文件
    const mtlFile = mtlFileName || project.name;
    this.output.push(`mtllib ${mtlFile}.mtl`);
    this.output.push('');

    // ===== 导出隐藏的交互层底平面（用于射线检测） =====
    this.exportHiddenGroundPlane(project);

    // 导出每个楼层
    project.floors.forEach((floor) => {
      this.output.push(`# Floor: ${floor.name}`);
      this.output.push('');

      // 导出楼层中的所有元素
      floor.layers.forEach((layer) => {
        if (!layer.visible) return;

        this.output.push(`# Layer: ${layer.name}`);
        
        layer.elements.forEach((element) => {
          switch (element.type) {
            case 'wall':
              this.exportWall(element as WallElement);
              break;
            case 'area':
              this.exportArea(element as AreaElement);
              break;
            case 'rectangle':
            case 'circle':
            case 'polygon':
              this.exportShape(element as any);
              break;
            case 'door':
              this.exportDoor(element as any);
              break;
            case 'stair':
              this.exportStair(element as any);
              break;
            case 'text':
              this.exportText(element as any);
              break;
          }
        });
      });
    });

    return this.output.join('\n');
  }

  /**
   * 导出隐藏的地面交互层
   * 用于射线检测和坐标拾取，在渲染时不可见
   */
  private exportHiddenGroundPlane(project: Project): void {
    this.output.push('# ==== Hidden Ground Plane (Interaction Layer) ====');
    this.output.push('# This plane is used for raycasting and coordinate picking');
    this.output.push('# It should be invisible in rendering but active for interactions');
    this.output.push('o HiddenGroundPlane');
    this.output.push('g InteractionLayer');
    this.output.push('usemtl HiddenGroundPlane_Material');

    const startIndex = this.vertexIndex;
    let points: { x: number; y: number }[] = [];

    // 优先级1: 使用mapConfig的maxX和maxY（地图实际大小）
    if (project.mapConfig?.maxX && project.mapConfig?.maxY) {
      const maxX = project.mapConfig.maxX;
      const maxY = project.mapConfig.maxY;
      
      points = [
        { x: 0, y: 0 },
        { x: maxX, y: 0 },
        { x: maxX, y: maxY },
        { x: 0, y: maxY },
      ];
      
      this.output.push(`# Using map config size: ${maxX}mm x ${maxY}mm`);
    }
    // 优先级2: 使用有效区域
    else if (project.mapConfig?.validArea && project.mapConfig.validArea.length >= 3) {
      points = project.mapConfig.validArea;
      this.output.push('# Using valid area polygon');
    }
    // 优先级3: 计算所有元素的边界框
    else {
      let minX = Infinity, minY = Infinity;
      let maxX = -Infinity, maxY = -Infinity;

      project.floors.forEach((floor) => {
        floor.layers.forEach((layer) => {
          layer.elements.forEach((element) => {
            element.points.forEach((p) => {
              minX = Math.min(minX, p.x);
              minY = Math.min(minY, p.y);
              maxX = Math.max(maxX, p.x);
              maxY = Math.max(maxY, p.y);
            });
          });
        });
      });

      // 如果有有效边界，创建矩形
      if (isFinite(minX) && isFinite(minY) && isFinite(maxX) && isFinite(maxY)) {
        // 扩展边界10%
        const paddingX = (maxX - minX) * 0.1;
        const paddingY = (maxY - minY) * 0.1;
        minX -= paddingX;
        minY -= paddingY;
        maxX += paddingX;
        maxY += paddingY;

        points = [
          { x: minX, y: minY },
          { x: maxX, y: minY },
          { x: maxX, y: maxY },
          { x: minX, y: maxY },
        ];
        
        this.output.push('# Using calculated bounding box with 10% padding');
      }
    }

    // 如果没有有效点，跳过
    if (points.length < 3) {
      this.output.push('# No valid area found, skipping ground plane');
      this.output.push('');
      return;
    }

    // 导出顶点（Y坐标为-0.01，避免与其他地面元素Z-fighting）
    points.forEach((point) => {
      const x = point.x / 100; // mm to meters
      const z = point.y / 100; // mm to meters, Z轴与2D的Y同向
      this.output.push(`v ${x.toFixed(6)} -0.010000 ${z.toFixed(6)}`);
      this.vertexIndex++;
    });

    // 使用三角剖分创建面（扇形三角剖分）
    for (let i = 1; i < points.length - 1; i++) {
      this.output.push(`f ${startIndex} ${startIndex + i} ${startIndex + i + 1}`);
    }

    this.output.push('');
  }

  /**
   * 创建立方体（辅助方法）
   * @param cx 中心X坐标
   * @param cy 中心Y坐标
   * @param cz 中心Z坐标
   * @param width 宽度
   * @param height 高度
   * @param depth 深度
   */
  private createBox(cx: number, cy: number, cz: number, width: number, height: number, depth: number): void {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const halfDepth = depth / 2;
    
    const startIndex = this.vertexIndex;
    
    // 8个顶点（立方体）
    const vertices = [
      [cx - halfWidth, cy - halfHeight, cz - halfDepth], // 0: 左下后
      [cx + halfWidth, cy - halfHeight, cz - halfDepth], // 1: 右下后
      [cx + halfWidth, cy + halfHeight, cz - halfDepth], // 2: 右上后
      [cx - halfWidth, cy + halfHeight, cz - halfDepth], // 3: 左上后
      [cx - halfWidth, cy - halfHeight, cz + halfDepth], // 4: 左下前
      [cx + halfWidth, cy - halfHeight, cz + halfDepth], // 5: 右下前
      [cx + halfWidth, cy + halfHeight, cz + halfDepth], // 6: 右上前
      [cx - halfWidth, cy + halfHeight, cz + halfDepth], // 7: 左上前
    ];
    
    vertices.forEach(([x, y, z]) => {
      this.output.push(`v ${x.toFixed(6)} ${y.toFixed(6)} ${z.toFixed(6)}`);
      this.vertexIndex++;
    });
    
    // 6个面（立方体）
    const faces = [
      [0, 1, 2, 3], // 后面
      [4, 7, 6, 5], // 前面
      [0, 4, 5, 1], // 底面
      [3, 2, 6, 7], // 顶面
      [0, 3, 7, 4], // 左面
      [1, 5, 6, 2], // 右面
    ];
    
    faces.forEach((face) => {
      const indices = face.map((i) => startIndex + i);
      this.output.push(`f ${indices.join(' ')}`);
    });
  }

  /**
   * 导出墙体
   */
  private exportWall(wall: WallElement): void {
    if (wall.points.length < 2) return;

    const thickness = wall.thickness / 1000 / 3; // mm to meters (减少到1/3厚度)
    const height = wall.height / 1000; // mm to meters，与3D预览中的墙高一致

    // 为每段墙创建独立的对象（修复路径规划障碍物提取问题）
    for (let i = 0; i < wall.points.length - 1; i++) {
      // 每段墙独立命名，避免整体边界框问题
      this.output.push(`o Wall_${wall.id}_seg${i}`);
      this.output.push(`g Wall_${wall.id}_seg${i}`);
      
      // 记录墙体透明度和颜色（供SDK渲染使用）
      const opacity = wall.opacity !== undefined ? wall.opacity : 1;
      this.output.push(`# WALL_OPACITY ${opacity}`);
      this.output.push(`# WALL_COLOR ${wall.color || '#d1d5db'}`);
      
      this.output.push(`usemtl Wall_${wall.id}`);
      const p1 = wall.points[i];
      const p2 = wall.points[i + 1];

      // 计算墙段的长度和角度
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      let length = Math.sqrt(dx * dx + dy * dy) / 100; // mm to meters
      
      // 跳过长度为0或极短的墙段（闭合时起点终点重合）
      if (length < 0.001) {
        continue;
      }
      
      // 扩展墙段长度以覆盖拐角处的缝隙
      const extension = thickness * 0.6;
      const isFirstSegment = i === 0;
      const isLastSegment = i === wall.points.length - 2;
      
      if (!isFirstSegment && !isLastSegment) {
        length += extension * 2;
      } else if (!isFirstSegment) {
        length += extension;
      } else if (!isLastSegment) {
        length += extension;
      }
      
      // 与预览/SDK一致：Z 轴与 2D 的 Y 同向（向下为正），使用 dy 直接计算角度
      const angle = Math.atan2(dy, dx);

      // 计算墙段中心点
      const centerX = (p1.x + p2.x) / 2 / 100;
      const centerZ = (p1.y + p2.y) / 2 / 100;

      // 创建墙段的8个顶点（长方体）
      const halfLength = length / 2;
      const halfThickness = thickness / 2;

      // 局部坐标系中的8个顶点
      const localVertices = [
        [-halfLength, 0, -halfThickness],
        [halfLength, 0, -halfThickness],
        [halfLength, height, -halfThickness],
        [-halfLength, height, -halfThickness],
        [-halfLength, 0, halfThickness],
        [halfLength, 0, halfThickness],
        [halfLength, height, halfThickness],
        [-halfLength, height, halfThickness],
      ];

      // 旋转并平移到世界坐标（右手系，Y 轴向上，rotation.y = angle）
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      
      const startIndex = this.vertexIndex;

      localVertices.forEach(([x, y, z]) => {
        // 绕Y轴旋转（右手系，Y 轴向上）：
        // x' = x*cos(θ) - z*sin(θ)
        // z' = x*sin(θ) + z*cos(θ)
        const rotatedX = x * cos - z * sin;
        const rotatedZ = x * sin + z * cos;
        
        // 平移
        const worldX = rotatedX + centerX;
        const worldY = y;
        const worldZ = rotatedZ + centerZ;

        this.output.push(`v ${worldX.toFixed(6)} ${worldY.toFixed(6)} ${worldZ.toFixed(6)}`);
        this.vertexIndex++;
      });

      // 添加面（长方体6个面）
      const faces = [
        [0, 1, 2, 3], // 前面
        [4, 7, 6, 5], // 后面
        [0, 4, 5, 1], // 底面
        [3, 2, 6, 7], // 顶面
        [0, 3, 7, 4], // 左面
        [1, 5, 6, 2], // 右面
      ];

      faces.forEach((face) => {
        const indices = face.map((i) => startIndex + i);
        this.output.push(`f ${indices.join(' ')}`);
      });
      
      // 每段墙后添加空行，分隔对象
      this.output.push('');
    }
  }

  /**
   * 导出区域（作为地面）
   * 使用ear clipping算法进行正确三角化，支持凹多边形
   */
  private exportArea(area: AreaElement): void {
    // 检查THREE是否可用
    if (typeof THREE === 'undefined') {
      console.error('[OBJExporter] THREE 未定义，无法导出区域');
      return;
    }

    if (!area.points || area.points.length < 3) return;

    this.output.push(`o Area_${area.id}`);
    this.output.push(`g Area_${area.id}`);
    this.output.push(`usemtl Area_${area.id}`);

    // 检查是否是校准边界区域
    const isCalibrationBoundary = area.properties?.isCalibrationBoundary === true;
    // SDK 解析：仅校准边界区域不显示；普通地面区域需保留（见 SceneCore._hideCalibrationBoundaryAreaMeshes）
    if (isCalibrationBoundary) {
      this.output.push(`# KIMAP_AREA_CALIBRATION_BOUNDARY ${area.id}`);
    }
    const yHeight = isCalibrationBoundary ? -0.020000 : 0.000000;

    // 创建Shape
    const shape = new THREE.Shape();
    const validPoints: { x: number; y: number }[] = [];
    area.points.forEach((point) => {
      // 安全检查：确保point存在且有有效的坐标
      if (!point || typeof point.x !== 'number' || typeof point.y !== 'number') {
        console.warn(`[OBJExporter] 区域 ${area.id} 包含无效的顶点数据:`, point);
        return;
      }
      validPoints.push({ x: point.x / 100, y: point.y / 100 });
    });

    // 检查有效点数
    if (validPoints.length < 3) {
      console.warn(`[OBJExporter] 区域 ${area.id} 有效顶点数不足，跳过导出`);
      return;
    }

    // 构建Shape路径
    validPoints.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x, point.y);
      } else {
        shape.lineTo(point.x, point.y);
      }
    });
    shape.closePath();

    // 使用Three.js的ShapeGeometry进行正确的ear clipping三角化
    let geometry: THREE.ShapeGeometry;
    try {
      geometry = new THREE.ShapeGeometry(shape);
    } catch (error) {
      console.error(`[OBJExporter] 区域 ${area.id} 创建ShapeGeometry失败:`, error);
      return;
    }

    // 验证几何体
    if (!geometry || !geometry.attributes || !geometry.attributes.position) {
      console.warn(`[OBJExporter] 区域 ${area.id} 几何体无效，跳过导出`);
      return;
    }

    // 使用正确的API访问属性（兼容新旧版本Three.js）
    const positionAttribute = geometry.attributes.position;
    const indexAttribute = geometry.index;

    // 检查几何体是否有效
    if (!positionAttribute || positionAttribute.count < 3) {
      console.warn(`[OBJExporter] 区域 ${area.id} 三角化后顶点数不足，跳过导出`);
      return;
    }

    // 记录当前顶点索引作为起始点（OBJ顶点是全局的）
    const startVertexIndex = this.vertexIndex;

    // 导出顶点
    for (let i = 0; i < positionAttribute.count; i++) {
      const x = positionAttribute.getX(i);
      const y = positionAttribute.getY(i);
      // ShapeGeometry在XY平面上，Y对应3D的Z
      this.output.push(`v ${x.toFixed(6)} ${yHeight.toFixed(6)} ${y.toFixed(6)}`);
      this.vertexIndex++;
    }

    // 导出三角面（索引需要加上起始索引，因为OBJ顶点是全局的）
    if (indexAttribute) {
      for (let i = 0; i < indexAttribute.count; i += 3) {
        // OBJ索引从1开始，需要加上起始索引
        const a = startVertexIndex + indexAttribute.getX(i);
        const b = startVertexIndex + indexAttribute.getX(i + 1);
        const c = startVertexIndex + indexAttribute.getX(i + 2);
        this.output.push(`f ${a} ${b} ${c}`);
      }
    } else {
      // 如果没有索引数组，使用默认的三角化顺序
      for (let i = 1; i < positionAttribute.count - 1; i++) {
        const a = startVertexIndex;
        const b = startVertexIndex + i;
        const c = startVertexIndex + i + 1;
        this.output.push(`f ${a} ${b} ${c}`);
      }
    }

    this.output.push('');
  }

  /**
   * 导出形状元素（矩形、圆形、多边形 - 家具等）
   */
  private exportShape(shape: any): void {
    if (shape.points.length === 0) return;

    // 家具高度按照与墙面的比例计算（与three-helper一致）
    const LOGICAL_WALL_HEIGHT = 3000; // 逻辑墙高3米
    const RENDER_WALL_HEIGHT = 0.375; // 渲染墙高375mm = 0.375m
    const furnitureLogicalHeight = shape.height || 750; // 家具真实高度（mm）
    const height = (furnitureLogicalHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT; // 按比例计算渲染高度
    
    // 计算离地高度（cm → 米，按比例缩放，与three-helper一致）
    const elevationCm = shape.elevation || 0;
    const elevationMm = elevationCm * 10; // cm → mm
    const elevation = (elevationMm / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;
    
    const type = shape.type;

    this.output.push(`o Shape_${type}_${shape.id}`);
    this.output.push(`g Shape_${shape.id}`);
    this.output.push(`# color ${shape.color || '#cccccc'}`);
    this.output.push(`# elevation ${elevationCm}`); // 记录离地高度（cm）
    // 导出透明度
    const shapeOpacity = shape.opacity !== undefined ? shape.opacity : 0.9;
    this.output.push(`# SHAPE_OPACITY ${shapeOpacity}`);
    this.output.push(`usemtl Shape_${type}_${shape.id}`);

    if (type === 'rectangle' && shape.points.length >= 4) {
      // 矩形/家具 - 使用BoxGeometry（立方体）
      // 使用所有4个角点计算边界框（与 three-helper.ts 保持一致）
      const minX = Math.min(...shape.points.map((p: any) => p.x));
      const maxX = Math.max(...shape.points.map((p: any) => p.x));
      const minY = Math.min(...shape.points.map((p: any) => p.y));
      const maxY = Math.max(...shape.points.map((p: any) => p.y));

      // 如果是家具，使用实际尺寸属性；否则使用边界框尺寸
      let width: number;
      let depth: number;

      if (shape.furnitureName && shape.width && shape.depth) {
        width = shape.width / 1000; // mm to meters
        depth = shape.depth / 1000;
      } else {
        width = Math.abs(maxX - minX) / 100;
        depth = Math.abs(maxY - minY) / 100;
      }

      // 中心位置（使用边界框中心）
      const centerX = (minX + maxX) / 2 / 100;
      const centerZ = (minY + maxY) / 2 / 100;

      // 创建立方体（与预览的BoxGeometry一致），应用离地高度
      this.createBox(centerX, height / 2 + elevation, centerZ, width, height, depth);
      
    } else if (type === 'circle' && shape.points.length >= 2) {
      // 圆形 - 使用CylinderGeometry（32边圆柱）
      const center = shape.points[0];
      const edge = shape.points[1];
      
      // 如果是圆形设备，使用实际半径；否则使用拖拽半径（与预览一致）
      let radius: number;
      
      if (shape.furnitureName && shape.radius) {
        radius = shape.radius / 1000; // mm to meters
      } else {
        radius = Math.sqrt(
          Math.pow(edge.x - center.x, 2) + Math.pow(edge.y - center.y, 2)
        ) / 100;
      }
      
      // 位置（与预览一致）
      const cx = center.x / 100;
      const cz = center.y / 100;
      
      // 创建圆柱体（32边，与预览一致）
      const segments = 32;
      const startIndex = this.vertexIndex;
      
      // 底部顶点（Y坐标应用离地高度）
      const bottomY = elevation;
      const topY = height + elevation;
      
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = cx + Math.cos(angle) * radius;
        const z = cz + Math.sin(angle) * radius;
        this.output.push(`v ${x.toFixed(6)} ${bottomY.toFixed(6)} ${z.toFixed(6)}`);
        this.vertexIndex++;
      }
      
      // 顶部顶点（Y坐标在height）
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2;
        const x = cx + Math.cos(angle) * radius;
        const z = cz + Math.sin(angle) * radius;
        this.output.push(`v ${x.toFixed(6)} ${topY.toFixed(6)} ${z.toFixed(6)}`);
        this.vertexIndex++;
      }
      
      // 底面
      const bottomIndices = [];
      for (let i = 0; i < segments; i++) {
        bottomIndices.push(startIndex + i);
      }
      this.output.push(`f ${bottomIndices.reverse().join(' ')}`);
      
      // 顶面
      const topIndices = [];
      for (let i = 0; i < segments; i++) {
        topIndices.push(startIndex + segments + i);
      }
      this.output.push(`f ${topIndices.join(' ')}`);
      
      // 侧面
      for (let i = 0; i < segments; i++) {
        const next = (i + 1) % segments;
        const b1 = startIndex + i;
        const b2 = startIndex + next;
        const t1 = startIndex + segments + i;
        const t2 = startIndex + segments + next;
        this.output.push(`f ${b1} ${b2} ${t2} ${t1}`);
      }
      
    } else {
      // 矩形/多边形：创建立方体或柱体
      if (shape.points.length < 3) return;
      
      const startIndex = this.vertexIndex;
      
      // 底部顶点（应用离地高度）
      shape.points.forEach((point: any) => {
        const x = point.x / 100;
        const z = point.y / 100;
        this.output.push(`v ${x.toFixed(6)} ${elevation.toFixed(6)} ${z.toFixed(6)}`);
        this.vertexIndex++;
      });
      
      // 顶部顶点（应用离地高度）
      shape.points.forEach((point: any) => {
        const x = point.x / 100;
        const z = point.y / 100;
        this.output.push(`v ${x.toFixed(6)} ${(height + elevation).toFixed(6)} ${z.toFixed(6)}`);
        this.vertexIndex++;
      });
      
      const pointCount = shape.points.length;
      
      // 底面（逆时针）
      const bottomFace = [];
      for (let i = pointCount - 1; i >= 0; i--) {
        bottomFace.push(startIndex + i);
      }
      this.output.push(`f ${bottomFace.join(' ')}`);
      
      // 顶面（顺时针）
      const topFace = [];
      for (let i = 0; i < pointCount; i++) {
        topFace.push(startIndex + pointCount + i);
      }
      this.output.push(`f ${topFace.join(' ')}`);
      
      // 侧面
      for (let i = 0; i < pointCount; i++) {
        const next = (i + 1) % pointCount;
        const b1 = startIndex + i;
        const b2 = startIndex + next;
        const t1 = startIndex + pointCount + i;
        const t2 = startIndex + pointCount + next;
        this.output.push(`f ${b1} ${b2} ${t2} ${t1}`);
      }
    }

    this.output.push('');
  }

  /**
   * 导出门
   */
  private exportDoor(door: any): void {
    if (door.points.length < 2) return;

    const width = (door.width || 900) / 100; // mm to meters
    
    // 门高度也要按照墙面比例计算
    const LOGICAL_WALL_HEIGHT = 3000; // 逻辑墙高3米
    const RENDER_WALL_HEIGHT = 0.375; // 渲染墙高0.375m
    const logicalDoorHeight = door.height || 2100;
    const height = (logicalDoorHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;
    
    const thickness = 0.05; // 5cm厚度

    this.output.push(`o Door_${door.id}`);
    this.output.push(`g Door_${door.id}`);
    this.output.push(`usemtl Door_${door.id}`);

    const p1 = door.points[0];
    const p2 = door.points[1];
    const centerX = (p1.x + p2.x) / 2 / 100;
    const centerZ = (p1.y + p2.y) / 2 / 100;

    // 计算门的朝向
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const angle = Math.atan2(dy, dx);

    const halfWidth = width / 2;
    const halfThickness = thickness / 2;

    // 创建门板的8个顶点
    const localVertices = [
      [-halfWidth, 0, -halfThickness],
      [halfWidth, 0, -halfThickness],
      [halfWidth, height, -halfThickness],
      [-halfWidth, height, -halfThickness],
      [-halfWidth, 0, halfThickness],
      [halfWidth, 0, halfThickness],
      [halfWidth, height, halfThickness],
      [-halfWidth, height, halfThickness],
    ];

    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const startIndex = this.vertexIndex;

    localVertices.forEach(([x, y, z]) => {
      const rotatedX = x * cos - z * sin;
      const rotatedZ = x * sin + z * cos;
      const worldX = rotatedX + centerX;
      const worldY = y;
      const worldZ = rotatedZ + centerZ;
      this.output.push(`v ${worldX.toFixed(6)} ${worldY.toFixed(6)} ${worldZ.toFixed(6)}`);
      this.vertexIndex++;
    });

    // 添加面
    const faces = [
      [0, 1, 2, 3],
      [4, 7, 6, 5],
      [0, 4, 5, 1],
      [3, 2, 6, 7],
      [0, 3, 7, 4],
      [1, 5, 6, 2],
    ];

    faces.forEach((face) => {
      const indices = face.map((i) => startIndex + i);
      this.output.push(`f ${indices.join(' ')}`);
    });

    this.output.push('');
  }

  /**
   * 导出文本元素（作为注释记录，供SDK渲染）
   */
  private exportText(text: any): void {
    if (!text.points || text.points.length === 0) return;
    if (!text.visible) return;

    this.output.push(`# ===== Text Element =====`);
    this.output.push(`# TEXT_ID ${text.id}`);
    this.output.push(`# TEXT_CONTENT ${text.content || ''}`);
    this.output.push(`# TEXT_POSITION ${text.points[0].x} ${text.points[0].y}`);
    this.output.push(`# TEXT_ELEVATION ${text.elevation || 8500}`);
    this.output.push(`# TEXT_FONT_SIZE ${text.fontSize || 16}`);
    this.output.push(`# TEXT_COLOR ${text.color || '#000000'}`);
    this.output.push(`# TEXT_FONT_FAMILY ${text.fontFamily || 'Arial, sans-serif'}`);
    this.output.push(`# TEXT_BOLD ${text.bold || false}`);
    this.output.push(`# TEXT_ITALIC ${text.italic || false}`);
    this.output.push(`# TEXT_BORDER_COLOR ${text.borderColor || '#87CEEB'}`);
    this.output.push(`# TEXT_BG_COLOR ${text.backgroundColor || '#FFFFFF'}`);
    this.output.push(`# TEXT_BG_OPACITY ${text.backgroundOpacity !== undefined ? text.backgroundOpacity : 0.8}`);
    this.output.push(`# TEXT_PADDING ${text.padding || 8}`);
    this.output.push(`# ===== End Text Element =====`);
    this.output.push('');
  }

  /**
   * 导出楼梯（与3D预览一致）
   */
  private exportStair(stair: any): void {
    if (stair.points.length < 2) return;

    this.output.push(`o Stair_${stair.id}`);
    this.output.push(`g Stair_${stair.id}`);
    this.output.push(`# color #ea580c`);
    this.output.push(`usemtl Stair_${stair.id}`);

    const p1 = stair.points[0];
    const p2 = stair.points[1];
    
    // 与预览一致的尺寸计算
    const width = Math.abs(p2.x - p1.x) / 100; // mm to meters
    const length = Math.abs(p2.y - p1.y) / 100;
    
    // 楼梯高度也要按照墙面比例计算
    const LOGICAL_WALL_HEIGHT = 3000; // 逻辑墙高3米
    const RENDER_WALL_HEIGHT = 0.375; // 渲染墙高0.375m
    const logicalStairHeight = stair.height || 3000;
    const totalHeight = (logicalStairHeight / LOGICAL_WALL_HEIGHT) * RENDER_WALL_HEIGHT;
    
    const steps = stair.steps || 15; // 默认15步，与预览一致

    const stepHeight = totalHeight / steps;
    const stepDepth = length / steps;

    const centerX = (p1.x + p2.x) / 2 / 100;
    const centerZ = (p1.y + p2.y) / 2 / 100;

    // 为每个台阶创建BoxGeometry（与预览一致）
    for (let i = 0; i < steps; i++) {
      const currentHeight = i * stepHeight;
      const currentDepth = i * stepDepth;
      
      // 台阶的中心位置（与预览一致）
      const stepCenterY = currentHeight + stepHeight / 2;
      const stepCenterZ = stair.direction === 'up' 
        ? -currentDepth - stepDepth / 2 
        : currentDepth + stepDepth / 2;
      
      // 使用createBox创建台阶，使用绝对位置
      this.createBox(
        centerX,        // X位置（楼梯中心）
        stepCenterY,    // Y位置（台阶中心）
        centerZ + stepCenterZ,  // Z位置（楼梯中心 + 相对偏移）
        width,          // 宽度
        stepHeight,     // 高度
        stepDepth       // 深度
      );
    }

    this.output.push('');
  }
}

/**
 * 导出项目为OBJ文件
 */
export function exportProjectAsOBJ(project: Project): void {
  // 生成唯一ID
  const uniqueId = generateKimapUniqueId();
  
  // 文件名直接使用uniqueId
  const fileName = uniqueId;

  const exporter = new OBJExporter();
  const objContent = exporter.exportProject(project, fileName);

  // 创建下载链接
  const blob = new Blob([objContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}.obj`;
  link.click();
  URL.revokeObjectURL(url);
  
  console.log('✅ OBJ导出成功：');
  console.log(`  - ${fileName}.obj`);
  console.log(`  - 文件ID: ${uniqueId}`);
}

/**
 * 导出项目为Kimap文件（加密格式）+ Theme材质文件
 */
export function exportProjectAsKimap(project: Project, providedUniqueId?: string): string {
  // 使用提供的ID或生成新的唯一ID
  const uniqueId = providedUniqueId || generateKimapUniqueId();
  
  // 文件名直接使用uniqueId
  const fileName = uniqueId;

  const exporter = new OBJExporter();
  const objContent = exporter.exportProject(project, fileName);

  // 导入加密工具和MTL导出器
  Promise.all([
    import('./kimapEncryption'),
    import('./mtlExporter')
  ]).then(([{ encryptToKimap }, { exportProjectAsMTL, encryptMTL }]) => {
    // 1. 导出并加密OBJ为.kimap
    const kimapContent = encryptToKimap(objContent);
    const kimapBlob = new Blob([kimapContent], { type: 'application/octet-stream' });
    const kimapUrl = URL.createObjectURL(kimapBlob);
    const kimapLink = document.createElement('a');
    kimapLink.href = kimapUrl;
    kimapLink.download = `${fileName}.kimap`;
    document.body.appendChild(kimapLink);
    kimapLink.click();
    document.body.removeChild(kimapLink);
    URL.revokeObjectURL(kimapUrl);

    // 2. 导出并加密MTL为.kitheme
    const mtlContent = exportProjectAsMTL(project);
    const themeContent = encryptMTL(mtlContent);
    const themeBlob = new Blob([themeContent], { type: 'application/octet-stream' });
    const themeUrl = URL.createObjectURL(themeBlob);
    const themeLink = document.createElement('a');
    themeLink.href = themeUrl;
    themeLink.download = `${fileName}.kitheme`;
    document.body.appendChild(themeLink);
    
    // 延迟下载，避免浏览器阻止
    setTimeout(() => {
      themeLink.click();
      document.body.removeChild(themeLink);
      URL.revokeObjectURL(themeUrl);
    }, 200);

    console.log('✅ 导出成功：');
    console.log(`  - ${fileName}.kimap (3D模型，包含墙面几何数据)`);
    console.log(`  - ${fileName}.kitheme (材质文件)`);
    console.log(`  - 文件ID: ${uniqueId}`);
  });
  
  // 返回uniqueId供调用者使用（例如导出kidata时使用相同的ID）
  return uniqueId;
}
